import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMount } from "react-use";
import { Box } from "@mui/material";
import * as Sentry from "@sentry/react";

import { HUBSPOT_PORTAL_ID } from "fond/constants";
import { HubSpotFormFieldValues, HubSpotOnReadyEvent, HubSpotOnSubmitSuccessEvent } from "fond/types/hubspot";
import { BlockSpinner } from "fond/widgets";

import { injectHubSpotScript } from "./util";

export type HubSpotFormProps = {
  /** The formId is the unique identifier for the HubSpot form you want to embed. */
  formId: string | undefined;
  /** The onReady callback is called when the HubSpot form is fully loaded and ready to be used. */
  onReady?: (event: HubSpotOnReadyEvent) => void;
  /** The onSubmit callback is called when the HubSpot form submission is successful. */
  onSubmit?: (event: HubSpotOnSubmitSuccessEvent) => void;
  /**
   * The initial data to inject into the form.
   *
   * This is a set of key/value pairs where the key is the field name in the HubSpot form.
   */
  initialData?: HubSpotFormFieldValues;
};

/**
 * HubSpotForm is a React component that embeds a HubSpot form into your application.
 * It uses the HubSpot Forms API to load and display the form.
 */
export const HubSpotForm: React.FC<HubSpotFormProps> = ({ formId, onReady, onSubmit, initialData }: HubSpotFormProps) => {
  const [showLoader, setShowLoader] = useState(true);
  const [showError, setShowError] = useState<string | null>(null);
  const scriptRef = useRef<HTMLScriptElement | null>(null);

  useMount(() => {
    if (!formId || !HUBSPOT_PORTAL_ID) {
      setShowError("This form is not configured correctly. Please contact fondhelp@biarrinetworks.com if this error persists.");
      Sentry.captureMessage("HubSpotForm: form ID and/or portal ID are not set.", "warning");
    }

    if (!scriptRef.current && !window.HubSpotFormsV4) {
      const scriptEl = injectHubSpotScript();
      if (scriptEl) {
        scriptRef.current = scriptEl;
      }
    }
  });

  /**
   * This function is called when the HubSpot form is ready.
   *
   * It removes the loading spinner once the form is fully loaded and sets the initial data if provided.
   */
  const handleOnReady = useCallback(
    (event: HubSpotOnReadyEvent) => {
      if (event.detail.formId === formId) {
        setShowLoader(false);
        onReady?.(event);
      }
      const form = window.HubSpotFormsV4?.getFormFromEvent(event);
      if (!form) {
        console.error(`HubSpot form with ID ${formId} not found.`);
        return;
      }
      // if we have data to set on the form, we can set it now.
      if (initialData) {
        Object.entries(initialData).forEach(([name, value]) => {
          form.setFieldValue(name, value);
        });
      }
    },
    [formId, onReady, initialData]
  ) as EventListener;

  /**
   * This function is called when the HubSpot form submission is successful.
   */
  const handleOnSubmit = useCallback(
    (event: HubSpotOnSubmitSuccessEvent) => {
      if (event.detail.formId === formId) {
        onSubmit?.(event);
      }
    },
    [formId, onSubmit]
  ) as EventListener;

  useEffect(() => {
    window.addEventListener("hs-form-event:on-ready", handleOnReady);
    window.addEventListener("hs-form-event:on-submission:success", handleOnSubmit);

    return () => {
      window.removeEventListener("hs-form-event:on-ready", handleOnReady);
      window.removeEventListener("hs-form-event:on-submission:success", handleOnSubmit);
    };
  }, [formId, handleOnReady, handleOnSubmit]);

  return (
    <Box minHeight={200} justifyContent="center" display="flex" flexDirection="column">
      {showError ? (
        <Box mx="auto" px={5}>
          {showError}
        </Box>
      ) : (
        showLoader && <BlockSpinner />
      )}
      {formId && <Box className="hs-form-frame" data-region="na1" data-form-id={formId} data-portal-id={HUBSPOT_PORTAL_ID} />}
    </Box>
  );
};
